<template>
  <!--    Contribution Form-->
  <validation-observer
    ref="contributionPaymentForm"
    v-slot="{invalid}"
  >
    <am-modal
      :title="`${updateContributionMode ? 'Update Payment' : 'Record Payment'}`"
      :show="contributionModalOpened"
      :show-overlay="deletingContribution"
      size="md"
      @onClose="closeContributionModal()"
    >
      <b-row class="align-items-start">
        <div class="col-7">
          <am-select
            v-model="selectedContributionPaymentId"
            placeholder="All Payments"
            :options="contributionPayments"
            label-name="payment_label"
            value-name="id"
            name="contributions"
            @input="populateContributionForm"
          />
        </div>
        <div class="col-2">
          <am-dropdown
            icon="TrashIcon"
            name="delete_contribution"
          >
            <b-dropdown-item
              id="cd_contribution"
              @click="deleteContribution()"
            >
              Yes
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item>
              No
            </b-dropdown-item>
          </am-dropdown>
        </div>
        <div class="col-2">
          <am-button
            name="contribution_action"
            variant="flat-danger"
            icon="XIcon"
            label=""
            tool-tip-text="Clear Form"
            @click="resetContributionForm()"
          />
        </div>
      </b-row>
      <error-display :error="error" />
      <am-select
        v-model="contribution.contribution_id"
        name="contribution_id"
        label="Contribution"
        rules="required"
        label-name="setting_label"
        value-name="id"
        :options="contributions"
        @input="onContributionChanged"
      />
      <div class="mb-1">
        <label>Contributor ID</label>
        <div class="d-flex align-items-start contributor-search">
          <b-form-input
            v-model="contributorSearch"
            placeholder="Search contributor by name or ID..."
            class="contributor-search__input"
            @keyup.enter="searchContributors"
          />
          <am-button
            class="contributor-search__btn ml-1"
            icon="SearchIcon"
            label="Search"
            variant="outline-primary"
            :loading="searchingContributor"
            :disabled="!contribution.contribution_id"
            @click="searchContributors"
          />
        </div>
        <div class="mt-1">
          <am-select
            v-model="contribution.contributor_id"
            name="contributor"
            label="Select contributor"
            rules="required"
            placeholder="Select a contributor from search results"
            label-name="label"
            value-name="contributor_id"
            :options="contributorOptions"
            :disabled="!contributorOptions.length"
            @input="onContributorSelected"
          />
        </div>
        <small
          v-if="contributorSearchDone && !contributorOptions.length"
          class="text-muted"
        >
          No contributors found. Try another search.
        </small>
        <div
          v-if="contributor"
          class="d-flex align-items-center mt-1 mb-1"
        >
          <am-profile-photo
            size="30"
            :photo="contributor.photo"
          />
          <div style="margin-right: 5px;" />
          <b-badge variant="light-primary">
            {{ contributor.name }}
          </b-badge>
          <am-button
            icon-only
            variant="flat-danger"
            icon="XIcon"
            @click="clearContributor()"
          />
        </div>
      </div>
      <am-input
        v-if="contributor"
        :value="totalToPayDisplay"
        name="total_to_pay"
        :label="`Total to pay (${currencySymbol})`"
        :suffix-icon="null"
        readonly
      />
      <am-input
        v-model="contribution.amount"
        name="amount"
        :label="`Amount (${currencySymbol})`"
        :rules="amountRules"
      />
      <am-select
        v-model="contribution.payment_mode"
        name="payment_mode"
        label="Payment Method"
        rules="required"
        :options="paymentModes"
      />
      <am-input
        v-if="showTransactionId"
        v-model="contribution.reference"
        name="reference"
        label="Transaction/Tracking ID"
      />
      <am-input
        v-model="contribution.payer_name"
        name="payer_name"
        label="Payer Name"
        rules="required"
      />
      <am-input
        v-model="contribution.payer_phone"
        name="payer_phone"
        label="Payer Phone"
        rules="required"
      />
      <am-date-picker
        id="payment_date"
        v-model="contribution.payment_date"
        name="payment_date"
        label="Payment Date"
        rules="required"
      />
      <template slot="modal-actions">
        <b-row class="float-right">
          <am-button
            name="cancel"
            variant="flat-danger"
            icon="XIcon"
            class="mr-1"
            label="Cancel"
            @click="closeContributionModal()"
          />
          <am-button
            name="save_contribution"
            :label="updateContributionMode ? 'Update' : 'Confirm & Generate Receipt'"
            :disabled="invalid"
            :loading="contributionLoading"
            loading-text="Loading..."
            @click="updateContributionMode?updateContribution():createContribution()"
          />
        </b-row>
      </template>
    </am-modal>
  </validation-observer>
</template>

<script>
import {
  BRow, BDropdownItem, BDropdownDivider, BBadge, BFormInput,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import logData from '@/libs/log'
import AmInput from '@/farmbusiness/components/AmInput.vue'
import AmSelect from '@/farmbusiness/components/AmSelect.vue'
import AmButton from '@/farmbusiness/components/AmButton.vue'
import AmDropdown from '@/farmbusiness/components/AmDropdown.vue'
import AmModal from '@/farmbusiness/components/AmModal.vue'
import showToast from '@/farmbusiness/mixins/showToast'
import downloader from '@/farmbusiness/mixins/downloader'
import ErrorDisplay from '@/farmbusiness/components/AmErrorDisplay.vue'
import AmDatePicker from '@/farmbusiness/components/AmDatePicker.vue'
import AmProfilePhoto from '@/farmbusiness/components/AmProfilePhoto.vue'
import { formatContributionLabel } from '@/farmbusiness/utils/helperFunctions'

const cont = {
  id: null,
  contribution_id: null,
  contributor_id: null,
  is_member_payment: false,
  amount: null,
  payment_mode: 'Cash',
  reference: null,
  payment_date: null,
  payer_name: null,
  payer_phone: null,
}
export default {
  name: 'ContributionPaymentSetup',
  components: {
    AmProfilePhoto,
    AmDatePicker,
    ErrorDisplay,
    AmModal,
    AmDropdown,
    AmButton,
    AmSelect,
    AmInput,
    ValidationObserver,
    BRow,
    BDropdownItem,
    BDropdownDivider,
    BBadge,
    BFormInput,
  },
  mixins: [showToast, downloader],
  props: {
    modalOpened: {
      type: Boolean,
      default: false,
    },
    id: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      error: {},
      contributionModalOpened: false,
      updateContributionMode: false,
      contributionLoading: false,
      deletingContribution: false,
      selectedContributionPaymentId: null,
      contribution: { ...cont },
      paymentModes: [
        'Cash',
        'Mobile Money',
        'Bank',
      ],
      contributorSearch: '',
      contributorOptions: [],
      searchingContributor: false,
      contributorSearchDone: false,
    }
  },
  computed: {
    memberContribution() {
      return this.selectedContributionSetting?.pricing_level?.includes('Member')
        || this.selectedContributionSetting?.transaction_type?.includes('Member')
        || this.selectedContributionSetting?.transaction_type?.includes('Pension')
    },
    showTransactionId() {
      return this.contribution.payment_mode === 'Mobile Money'
        || this.contribution.payment_mode === 'Bank'
    },
    totalToPay() {
      if (!this.contributor || this.contributor.amount == null) {
        return null
      }
      return parseFloat(this.contributor.amount)
    },
    totalToPayDisplay() {
      if (this.totalToPay == null) {
        return ''
      }
      if (this.totalToPay <= 0) {
        return '0.00 (Fully Paid)'
      }
      return Number(this.totalToPay).toFixed(2)
    },
    amountRules() {
      if (this.totalToPay != null && this.totalToPay >= 0) {
        return `required|numeric|min_value:0.01|max_value:${this.totalToPay}`
      }
      return 'required|numeric|min_value:0.01'
    },
    currencySymbol() {
      return 'GHS'
    },
    selectedContributionSetting() {
      return this.contributions.find(o => +o.id === +this.contribution.contribution_id)
    },
    selectedContributionPayment() {
      const contribution = this.contributionPayments.find(o => o.id === this.selectedContributionPaymentId)
      if (!contribution) {
        return null
      }
      return {
        ...contribution,
      }
    },
    paymentSettingId() {
      return this.$store.getters['contributionPayments/paymentSettingId']
    },
    contributions() {
      return (this.$store.getters['contributionSettings/contributionSettings'] || []).map(item => ({
        ...item,
        setting_label: formatContributionLabel(item.setting_label),
      }))
    },
    contributionPayments() {
      return this.$store.getters['contributionPayments/contributionPayments']
    },
    contributor() {
      return this.$store.getters['contributionPayments/contributor']
    },
  },
  watch: {
    modalOpened(opened) {
      this.contributionModalOpened = opened
      if (opened) {
        this.applyDefaultContribution()
      } else {
        this.clearContributor()
      }
    },
    id(id) {
      this.selectedContributionPaymentId = id
    },
    paymentSettingId() {
      this.applyDefaultContribution()
    },
  },
  mounted() {
    this.applyDefaultContribution()
  },
  methods: {
    applyDefaultContribution() {
      const id = this.paymentSettingId
      if (id && id !== 'all') {
        this.contribution.contribution_id = id
      } else if (!this.contribution.contribution_id || this.contribution.contribution_id === 'all') {
        this.contribution.contribution_id = null
      }
    },
    clearContributor() {
      this.contribution.contributor_id = null
      this.contributorSearch = ''
      this.contributorOptions = []
      this.contributorSearchDone = false
      this.$store.commit('contributionPayments/setContributor', null)
    },
    onContributionChanged() {
      this.clearContributor()
    },
    resetContributionForm() {
      this.contribution = { ...cont }
      this.selectedContributionPaymentId = null
      this.updateContributionMode = false
      this.applyDefaultContribution()
      this.clearContributor()
      this.$refs.contributionPaymentForm.reset()
    },
    populateContributionForm() {
      if (this.selectedContributionPayment) {
        this.updateContributionMode = true
        this.contribution = {
          ...cont,
          ...this.selectedContributionPayment,
          payment_mode: this.selectedContributionPayment.payment_mode || 'Cash',
        }
        if (this.contribution.contributor_id) {
          this.contributorOptions = [{
            contributor_id: this.contribution.contributor_id,
            label: this.contribution.contributor_id,
          }]
          this.getContributorDetails()
        }
      } else {
        this.resetContributionForm()
      }
    },
    closeContributionModal() {
      this.contributionModalOpened = false
      this.$emit('modalClosed')
    },
    searchContributors() {
      if (!this.contribution.contribution_id) {
        this.showInfo('Please select a contribution first')
        return
      }
      const term = (this.contributorSearch || '').trim()
      if (!term) {
        this.showError('Enter a contributor name or ID to search')
        return
      }
      this.searchingContributor = true
      this.contributorSearchDone = false
      const action = this.memberContribution
        ? 'contributionSettings/searchMembers'
        : 'contributionSettings/searchOrganizations'
      this.$store.dispatch(action, { search: term, limit: 8 })
        .then(results => {
          this.contributorOptions = results || []
          this.contributorSearchDone = true
          if (this.contribution.contributor_id
            && !this.contributorOptions.some(o => String(o.contributor_id) === String(this.contribution.contributor_id))) {
            this.contribution.contributor_id = null
            this.$store.commit('contributionPayments/setContributor', null)
          }
          if (this.contributorOptions.length === 1) {
            this.contribution.contributor_id = this.contributorOptions[0].contributor_id
            this.onContributorSelected(this.contribution.contributor_id)
          }
        })
        .catch(() => {
          this.showError('Failed to search contributors')
        })
        .finally(() => {
          this.searchingContributor = false
        })
    },
    onContributorSelected(id) {
      if (!id) {
        this.$store.commit('contributionPayments/setContributor', null)
        return
      }
      this.getContributorDetails()
    },
    getContributorDetails() {
      const id = this.contribution.contributor_id
      if (!id) {
        this.showInfo('Enter Contributor ID')
        return
      }
      if (!this.contribution.contribution_id) {
        this.showInfo('Please select a contribution first')
        return
      }
      this.$store.dispatch('contributionPayments/getAmountToPay', {
        contributor_id: id,
        contribution_id: this.contribution.contribution_id,
        is_member: this.memberContribution,
      }).catch(err => {
        if (err.response && err.response.status === 404) {
          this.showInfo('Contributor not found')
        } else {
          this.showError('Failed to get amount to pay')
        }
      })
    },
    createContribution() {
      this.error = {}
      if (this.totalToPay != null && parseFloat(this.contribution.amount) > this.totalToPay + 0.0001) {
        this.showError(`Amount cannot exceed total to pay (${this.totalToPay})`)
        return
      }
      this.contributionLoading = true
      this.contribution.is_member_payment = this.memberContribution
      if (!this.showTransactionId) {
        this.contribution.reference = null
      }
      this.$store.dispatch('contributionPayments/createContribution', this.contribution)
        .then(payment => {
          this.showSuccess('Payment recorded successfully')
          return this.$store.dispatch('contributionPayments/generateReceipts', {
            id: payment.contribution_id,
            ids: [payment.id],
          }).then(url => {
            if (url) {
              this.downloadFile(url, 'Payment Receipt')
            }
          }).catch(() => {
            this.showError('Payment saved but failed to generate receipt')
          })
        })
        .then(() => {
          this.resetContributionForm()
        })
        .catch(err => {
          logData(err)
          this.error = err
          this.showError('Failed to record payment')
        }).finally(() => {
          this.contributionLoading = false
        })
    },
    updateContribution() {
      this.error = {}
      if (!this.contribution.id) {
        return
      }
      this.contributionLoading = true
      if (!this.showTransactionId) {
        this.contribution.reference = null
      }
      this.$store.dispatch('contributionPayments/updateContribution', this.contribution)
        .then(() => {
          this.showSuccess('Updated contribution successfully')
        })
        .catch(err => {
          logData(err)
          this.error = err
          this.showError('Failed to update contribution')
        }).finally(() => {
          this.contributionLoading = false
        })
    },
    deleteContribution() {
      if (!this.selectedContributionPaymentId) {
        this.showInfo('Please select a contribution')
        return
      }
      this.deletingContribution = true
      this.$store.dispatch('contributionPayments/deleteContribution', this.selectedContributionPaymentId)
        .then(() => {
          this.showSuccess('Deleted Successfully')
          this.resetContributionForm()
        })
        .catch(() => {
          this.showError('Failed to delete contribution')
        }).finally(() => {
          this.deletingContribution = false
        })
    },
  },
}
</script>

<style scoped>
.contributor-search {
  width: 100%;
}
.contributor-search__input {
  flex: 1;
}
.contributor-search__btn {
  flex-shrink: 0;
}
.contributor-search__btn >>> .btn,
.contributor-search__btn >>> div {
  height: 100%;
}
</style>
